import React, { useEffect, useState } from "react";

import {
  QUIZ_FIELD_MAX,
  QUIZ_OPTION_MAX,
  QUIZ_POINTS_MAX,
  QUIZ_POINTS_MIN,
  clampQuizFields,
  clampQuizPoints,
  emptyQuizQuestion,
  validateQuizRow,
} from "./quizHelpers";

const OPTION_KEYS = ["option_one", "option_two", "option_three"];
const OPTION_POINT_KEYS = ["option_one_points", "option_two_points", "option_three_points"];
const OPTION_LABELS = ["Option one", "Option two", "Option three"];

export default function QuizQuestionModal({
  open,
  mode,
  initial,
  onClose,
  onSubmit,
  isSaving,
}) {
  const [draft, setDraft] = useState(() => emptyQuizQuestion("temp"));
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!open) return;
    const base = initial
      ? { id: initial.id, ...clampQuizFields(initial), correct_answer: "" }
      : emptyQuizQuestion(`new-${Date.now()}`);
    setDraft(base);
    setLocalError("");
  }, [open, mode, initial]);

  if (!open) return null;

  const setField = (field, value) => {
    const max = field === "question_name" ? QUIZ_FIELD_MAX : QUIZ_OPTION_MAX;
    setDraft((prev) => ({ ...prev, [field]: value.slice(0, max) }));
    setLocalError("");
  };

  const setPointsField = (field, value) => {
    const digits = String(value).replace(/\D/g, "");
    if (digits === "") {
      setDraft((prev) => ({ ...prev, [field]: "" }));
      setLocalError("");
      return;
    }
    const n = parseInt(digits, 10);
    const capped = Math.min(QUIZ_POINTS_MAX, Math.max(QUIZ_POINTS_MIN, n));
    setDraft((prev) => ({ ...prev, [field]: String(capped) }));
    setLocalError("");
  };

  const blurPointsField = (field) => {
    setDraft((prev) => ({
      ...prev,
      [field]: clampQuizPoints(prev[field]),
    }));
  };

  const finalize = async () => {
    const check = validateQuizRow({ ...draft, correct_answer: "" });
    if (!check.ok) {
      setLocalError(check.message);
      return;
    }
    try {
      await onSubmit({ id: draft.id, ...check.data, correct_answer: "" });
    } catch {
      /* parent surfaces error */
    }
  };

  const isEdit = mode === "edit";

  return (
    <div className="quiz-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="quiz-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quiz-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="quiz-modal-header">
          <h2 id="quiz-modal-title">{isEdit ? "Edit Question" : "New Question"}</h2>
          <button type="button" className="quiz-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="quiz-modal-body">
          {localError ? <p className="cr-alert cr-alert-error quiz-modal-error">{localError}</p> : null}

          <div className="quiz-modal-field quiz-modal-field--full">
            <label htmlFor="quiz-modal-question">Question</label>
            <textarea
              id="quiz-modal-question"
              className="quiz-modal-textarea"
              value={draft.question_name}
              maxLength={QUIZ_FIELD_MAX}
              onChange={(e) => setField("question_name", e.target.value)}
              placeholder="Question"
              rows={4}
            />
            <p className="quiz-modal-limit">Max length {QUIZ_FIELD_MAX} characters</p>
          </div>

          <div className="quiz-modal-options-grid">
            {OPTION_KEYS.map((key, i) => {
              const pointsKey = OPTION_POINT_KEYS[i];
              return (
                <div key={key} className="quiz-modal-field">
                  <label htmlFor={`quiz-modal-${key}`}>{OPTION_LABELS[i]}</label>
                  <input
                    id={`quiz-modal-${key}`}
                    type="text"
                    value={draft[key]}
                    maxLength={QUIZ_OPTION_MAX}
                    onChange={(e) => setField(key, e.target.value)}
                    placeholder={OPTION_LABELS[i]}
                  />
                  <label htmlFor={`quiz-modal-${pointsKey}`} className="quiz-modal-points-label">
                    Points for this option
                  </label>
                  <input
                    id={`quiz-modal-${pointsKey}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className="quiz-modal-points-input"
                    value={draft[pointsKey] ?? ""}
                    onChange={(e) => setPointsField(pointsKey, e.target.value)}
                    onBlur={() => blurPointsField(pointsKey)}
                  />
                  <p className="quiz-modal-limit">
                    Points {QUIZ_POINTS_MIN}–{QUIZ_POINTS_MAX} · Max length {QUIZ_OPTION_MAX} characters
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="quiz-modal-footer">
          <button
            type="button"
            className="quiz-modal-btn quiz-modal-btn-save"
            onClick={finalize}
            disabled={isSaving}
          >
            <i className="fa-solid fa-cloud-arrow-up" aria-hidden />
            {isEdit ? "Update" : "Add New Question"}
          </button>
          <button type="button" className="quiz-modal-btn quiz-modal-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
